<template>
  <div>
    <b-modal
        v-model="showModalInternal"
        id="setting-overrides-modal"
        scrollable
        :title="`Nadpisania dla: ${selectedSetting?.displayedName || selectedSetting?.name}`"
        title-class="font-18"
        size="xl"
        hide-footer
    >
      <div v-if="selectedSetting" class="p-2">
        <div class="mb-3">
          <span class="text-muted">Globalna wartość: </span>
          <span class="fw-bold">{{ selectedSetting.value }}</span>
        </div>
        <div class="mb-3 justify-right">
          <button class="btn btn-sm btn-primary" @click="openDrugstoreSelect">
            <i class="fas fa-plus me-1"></i> Dodaj nadpisanie
          </button>
        </div>


        <SearchBar2
            :searchOptions="searchOptions"
            @searchRequest="handleSearch"
        />

        <CommonTable2
            ref="overridesTable"
            :table="tableData"
            :key="tableData.data"
            rowIdentifier="drugstoreClientId"
            :perPage="pagination.size"
            :currentPage="pagination.number + 1"
            :totalRows="pagination.totalElements"
            :totalPages="pagination.totalPages"
            :showPaginationTop="true"
            :showPaginationBottom="true"
            :showMassActions="true"
            :showRowActions="true"
            :enableRowSelection="false"
            :multiselect="true"
            @onPageChange="handlePageChange"
            @onSort="handleSort"
            @onItemAction="handleItemAction"
            @onMassAction="handleMassAction"
        />

      </div>
    </b-modal>

    <!-- Modal for entering value during creation/edit/mass change -->
    <b-modal
        v-model="showValueModal"
        id="override-value-modal"
        :title="valueModalTitle"
        @ok="handleValueModalOk"
    >
      <div v-if="selectedSetting" class="form-group">
        <label>Wartość nadpisania:</label>
        <div v-if="selectedSetting.settingType === 'BOOLEAN'" class="form-check form-switch mt-2">
          <input
              class="form-check-input"
              type="checkbox"
              v-model="editValue"
          />
          <label class="form-check-label">{{ editValue ? 'PRAWDA' : 'FAŁSZ' }}</label>
        </div>
        <input
            v-else-if="selectedSetting.settingType === 'DOUBLE' || selectedSetting.settingType === 'INTEGER'"
            type="number"
            class="form-control"
            v-model="editValue"
            :step="selectedSetting.settingType === 'DOUBLE' ? '0.01' : '1'"
        />
        <input
            v-else
            type="text"
            class="form-control"
            v-model="editValue"
        />
      </div>
    </b-modal>

    <DrugstoreOrGroupsTabsModal
        v-if="showDrugstoreSelectModal"
        :showModal="showDrugstoreSelectModal"
        @update:showModal="showDrugstoreSelectModal = $event"
        title="Wybierz apteki dla nadpisania"
        ok-title="Wybierz"
        @onApply="handleDrugstoreSelection"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { BModal } from 'bootstrap-vue-next';
import CommonTable2 from "@/components/widgets/CommonTable2.vue";
import SearchBar2 from "@/components/widgets/SearchBar2.vue";
import DrugstoreOrGroupsTabsModal from "@/views/DrugstoreCommonManager/DrugstoreOrGroupsTabsModal.vue";
import { useCommonAppModule } from "@/state/pinia/commonAppModule";
import { useLazyLoaderModule } from "@/state/pinia/lazyloaderManagerModule";
import Swal from "sweetalert2";
import { apiRequestsHelper } from "@/state/pinia/apiRequestHelper";

export default defineComponent({
  name: "SettingOverridesModal",
  components: {
    BModal,
    CommonTable2,
    SearchBar2,
    DrugstoreOrGroupsTabsModal
  },
  props: {
    showModal: { type: Boolean, required: true },
    selectedSetting: { type: Object, required: false, default: null }
  },
  emits: ['update:showModal', 'onClose'],
  data() {
    return {
      showModalInternal: this.showModal,
      tableData: {
        headers: [
          { key: "drugstoreClientId", text: "ID Apteki", type: "text", sortable: true },
          { key: "drugstoreName", text: "Nazwa Apteki", type: "text", sortable: true },
          { key: "workstationId", text: "ID Stanowiska", type: "text", sortable: true },
          { key: "value", text: "Wartość", type: "text", sortable: true },
          {
            text: "Akcje",
            key: "actions",
            type: "actions",
            actions: [
              { action:"edit-override", enabled: true, text: "Edytuj", icon: "fas fa-edit", tooltip: "Edytuj nadpisanie", class: "btn btn-info btn-sm table-actions-button edit-button" },
              { action: "delete-override", enabled: true, text: "Usuń", icon: "fas fa-trash",   tooltip: "Usuń nadpisanie", class: "btn btn-danger btn-sm table-actions-button edit-button"}
            ],
          },

        ],
        data: [],
        massActions: [
          { key: "mass-edit-override", text: "Masowa zmiana", icon: "fas fa-edit", class: "btn btn-info btn-sm table-actions-button edit-button", enabled: true },
          { key: "mass-delete-override", text: "Masowe usuwanie", icon: "fas fa-trash",class: "btn btn-danger btn-sm table-actions-button edit-button", enabled: true }
        ]
      },
      pagination: {
        number: 0,
        size: 10,
        totalElements: 0,
        totalPages: 0
      },
      sorting: {
        key: "drugstore.drugstoreClientId",
        direction: "ASC"
      },
      searchRequest: {
        searchPhrase: "",
        searchKey: "drugstore.drugstoreClientId"
      },
      searchOptions: [
        { key: "drugstore.drugstoreClientId", name: "ID Apteki" },
        { key: "drugstore.drugstoreName", name: "Nazwa Apteki" }
      ],
      showDrugstoreSelectModal: false,
      showValueModal: false,
      valueModalTitle: "",
      valueModalMode: "", // 'create', 'edit', 'mass-edit'
      editValue: "",
      selectedDrugstoreClientIds: [],
      currentEditOverride: null,
      selectedItemsForMassAction: []
    };
  },
  computed: {
    commonModule() { return useCommonAppModule(); },
    lazyloaderModule() { return useLazyLoaderModule(); }
  },
  methods: {
    fetchOverrides() {
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.commonModule.httpGetAppSettingOverridesBySettingNamePage(
          this.selectedSetting.name,
          this.pagination.number,
          this.sorting.key,
          this.sorting.direction,
          this.searchRequest
      ).then(response => {
        if (response.status === 200) {
          console.log("Fetching ovverides: ", response.data.payload)
          const page = response.data.payload;
          this.tableData.data = page.content;
          this.pagination.totalElements = page.totalElements;
          this.pagination.totalPages = page.totalPages;
          this.pagination.number = page.number;
        }
      }).finally(() => {
        this.lazyloaderModule.setDisplayLazyLoader(false);
      });
    },
    handlePageChange(page) {
      this.pagination.number = page - 1;
      this.fetchOverrides();
    },
    handleSort(event) {
      this.sorting.key = event.column;
      if(sorting.key === 'drugstoreClientId' || sorting.key === 'drugstoreName'){
        this.sorting.key = 'drugstore.' + sorting.key;
      }

      this.sorting.direction = event.direction;
      this.fetchOverrides();
    },
    handleSearch(searchRequest) {
      if(searchRequest.searchKey === 'drugstoreClientId' || searchRequest.searchKey === 'drugstoreName'){
        searchRequest.searchKey = 'drugstore.' + searchRequest.searchKey;
      }

      this.searchRequest = searchRequest;
      this.pagination.number = 0;
      this.fetchOverrides();
    },
    handleItemAction(event) {
      console.log("Item action:", event)
      const action = event.action;
      const item = event.itemData;
      if (action.action === 'edit-override') {
        this.openEditValue(item);
      } else if (action.action === 'delete-override') {
        this.confirmDelete(item);
      }
    },
    handleMassAction(event) {
      const action = event.action;
      const items = event.itemsData;
      if (items.length === 0) return;

      if (action.key === 'mass-edit-override') {
        this.openMassEditValue(items);
      } else if (action.key === 'mass-delete-override') {
        this.confirmMassDelete(items);
      }
    },
    openDrugstoreSelect() {
      this.showDrugstoreSelectModal = true;
    },
    handleDrugstoreSelection(payload) {
      if (!payload || !payload.items || payload.items.length === 0) return;

      if (payload.type === 'DRUGSTORES') {
        this.selectedDrugstoreClientIds = payload.items.map(i => i.drugstoreClientId);
        this.openCreateValue();
      } else if (payload.type === 'GROUPS') {
        const drugstoresClientIds = [];
        payload.items.forEach(g => {
          if (g.drugstoresClientIds) drugstoresClientIds.push(...g.drugstoresClientIds);
        });
        this.selectedDrugstoreClientIds = drugstoresClientIds;
        this.openCreateValue();
      }
    },
    openCreateValue() {
      this.valueModalTitle = "Ustaw wartość dla wybranych aptek";
      this.valueModalMode = 'create';
      this.editValue = this.selectedSetting.value;
      this.showValueModal = true;
    },
    openEditValue(item) {
      console.log("Open Edit modal:", item)
      this.currentEditOverride = item;
      this.valueModalTitle = `Edytuj wartość dla apteki ${item.drugstoreClientId}`;
      this.valueModalMode = 'edit';
      this.editValue = item.value;
      this.showValueModal = true;
    },
    openMassEditValue(items) {
      this.selectedItemsForMassAction = items;
      this.valueModalTitle = "Masowa zmiana wartości dla zaznaczonych";
      this.valueModalMode = 'mass-edit';
      this.editValue = "";
      this.showValueModal = true;
    },
    handleValueModalOk() {
      if (this.valueModalMode === 'create') {
        this.saveBulkOverrides();
      } else if (this.valueModalMode === 'edit') {
        this.saveSingleOverride();
      } else if (this.valueModalMode === 'mass-edit') {
        this.saveMassChangeValue();
      }
    },
    saveBulkOverrides() {
      const request = {
        settingName: this.selectedSetting.name,
        value: this.editValue.toString(),
        drugstoreClientIds: this.selectedDrugstoreClientIds
      };
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.commonModule.httpSaveAppSettingOverrideForDrugstores(request).then(response => {
        apiRequestsHelper.handleResponseWithDialogs(response, true);
        this.fetchOverrides();
      }).finally(() => {
        this.lazyloaderModule.setDisplayLazyLoader(false);
      });
    },
    saveSingleOverride() {
      const dto = {
        settingName: this.currentEditOverride.settingName,
        drugstoreClientId: this.currentEditOverride.drugstoreClientId,
        workstationId: this.currentEditOverride.workstationId,
        value: this.editValue.toString()
      };
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.commonModule.httpSaveAppSettingOverride(dto).then(response => {
        apiRequestsHelper.handleResponseWithDialogs(response, true);
        this.fetchOverrides();
      }).finally(() => {
        this.lazyloaderModule.setDisplayLazyLoader(false);
      });
    },
    saveMassChangeValue() {
      const request = {
        settingName: this.selectedSetting.name,
        value: this.editValue.toString(),
        drugstoreClientIds: this.selectedItemsForMassAction.map(i => i.drugstoreClientId)
      };
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.commonModule.httpSaveAppSettingOverrideForDrugstores(request).then(response => {
        apiRequestsHelper.handleResponseWithDialogs(response, true);
        this.fetchOverrides();
        if (this.$refs.overridesTable) this.$refs.overridesTable.selectedItems = [];
      }).finally(() => {
        this.lazyloaderModule.setDisplayLazyLoader(false);
      });
    },
    confirmDelete(item) {
      Swal.fire({
        title: "Czy na pewno usunąć?",
        text: `Nadpisanie dla apteki ${item.drugstoreClientId} zostanie usunięte.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Tak, usuń",
        cancelButtonText: "Anuluj"
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteOverride(item);
        }
      });
    },
    deleteOverride(item) {
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.commonModule.httpDeleteAppSettingOverride(item.settingName, item.drugstoreClientId, item.workstationId)
          .then(response => {
            apiRequestsHelper.handleResponseWithDialogs(response, true);
            this.fetchOverrides();
          }).finally(() => {
        this.lazyloaderModule.setDisplayLazyLoader(false);
      });
    },
    confirmMassDelete(items) {
      Swal.fire({
        title: "Czy na pewno usunąć zaznaczone?",
        text: `Zostanie usuniętych ${items.length} nadpisań.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Tak, usuń",
        cancelButtonText: "Anuluj"
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteOverridesBulk(items);
        }
      });
    },
    deleteOverridesBulk(items) {
      const itemsToDelete = items.map(i => ({
        settingName: i.settingName,
        drugstoreClientId: i.drugstoreClientId,
        workstationId: i.workstationId
      }));
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.commonModule.httpDeleteAppSettingOverridesBulk(itemsToDelete).then(response => {
        apiRequestsHelper.handleResponseWithDialogs(response, true);
        this.fetchOverrides();
        if (this.$refs.overridesTable) this.$refs.overridesTable.selectedItems = [];
      }).finally(() => {
        this.lazyloaderModule.setDisplayLazyLoader(false);
      });
    }
  },
  watch: {
    showModal(val) {
      this.showModalInternal = val;
      if (val) this.fetchOverrides();
    },
    showModalInternal(val) {
      this.$emit('update:showModal', val);
      if (!val) this.$emit('onClose');
    }
  },
  created() {
    if (this.showModal) this.fetchOverrides();
  }
});
</script>

<style scoped>
</style>
